'use client';

import { Shield, Code } from 'lucide-react';
import { getSeverityColor } from '../utils/nvdApi';

interface CVESummary {
  id: string;
  description: string;
  cvss_v3_score: number | null;
  cvss_v3_severity: string | null;
  published: string;
}

interface RelatedCVEsProps {
  currentCveId: string;
  relatedByCWE: CVESummary[];
  relatedBySeverity: CVESummary[];
  cweIds?: string[];
  severity?: string | null;
}

function CVEMiniCard({ cve }: { cve: CVESummary }) {
  return (
    <a
      href={`/tools/security/cve-lookup/${cve.id.toLowerCase()}`}
      className="block p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
    >
      <div className="flex items-center justify-between gap-2 mb-1">
        <span className="font-semibold text-blue-600 dark:text-blue-400 text-sm">
          {cve.id}
        </span>
        {cve.cvss_v3_severity && (
          <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getSeverityColor(cve.cvss_v3_severity)}`}>
            {cve.cvss_v3_severity} {cve.cvss_v3_score}
          </span>
        )}
      </div>
      <p className="text-xs text-gray-600 dark:text-gray-400 line-clamp-2">
        {cve.description}
      </p>
      <div className="text-xs text-gray-500 dark:text-gray-500 mt-1">
        {new Date(cve.published).toLocaleDateString()}
      </div>
    </a>
  );
}

export default function RelatedCVEs({
  currentCveId,
  relatedByCWE,
  relatedBySeverity,
  cweIds,
  severity
}: RelatedCVEsProps) {
  // Don't render if there are no related CVEs
  if (relatedByCWE.length === 0 && relatedBySeverity.length === 0) {
    return null;
  }

  // Get valid CWE IDs for display
  const validCweIds = cweIds?.filter(id => /^CWE-\d+$/i.test(id)) || [];

  return (
    <section className="mt-8 border-t border-gray-200 dark:border-gray-700 pt-8">
      <h3 className="text-lg font-semibold mb-4">Related Vulnerabilities</h3>

      <div className="grid md:grid-cols-2 gap-6">
        {/* Related by CWE */}
        {relatedByCWE.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3 flex items-center gap-2">
              <Code className="w-4 h-4" />
              Same Weakness Type
              {validCweIds.length > 0 && (
                <span className="text-xs text-gray-500">
                  ({validCweIds.slice(0, 2).join(', ')}{validCweIds.length > 2 ? '...' : ''})
                </span>
              )}
            </h4>
            <div className="space-y-2">
              {relatedByCWE.map(cve => <CVEMiniCard key={cve.id} cve={cve} />)}
            </div>
          </div>
        )}

        {/* Related by Severity */}
        {relatedBySeverity.length > 0 && (
          <div>
            <h4 className="text-sm font-medium text-gray-600 dark:text-gray-400 mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Similar Severity
              {severity && (
                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${getSeverityColor(severity)}`}>
                  {severity}
                </span>
              )}
            </h4>
            <div className="space-y-2">
              {relatedBySeverity.map(cve => <CVEMiniCard key={cve.id} cve={cve} />)}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
